import { api } from './api';

export interface ContactSummaryView {
  attempts: number; conversations: number; inbound: number;
  lastAttemptAt: string | null; hasOptedOut: boolean; looksUnreachable: boolean; words: string;
}

export interface ContactAttemptView {
  channel: string; direction: string; outcome: string;
  note: string | null; at: string; byUserId: string | null;
}

export interface EnquiryView {
  id: string; reference: string; applicantName: string; seekingClass: string;
  guardianName: string; guardianMobile: string; guardianEmail: string | null;
  stage: string; isClosed: boolean; isThinCapture: boolean;
  channel: string; channelLabel: string; campaign: string | null;
  ownerUserId: string | null; nextFollowUpOn: string | null;
  missingForApplication: string[];
  contact: ContactSummaryView;
  attempts: ContactAttemptView[];
  createdAt: string;
}

export interface SourceLineView {
  key: string; channel: string; channelLabel: string; campaign: string | null;
  enquiries: number; admitted: number; stillOpen: number; conversionPercent: number;
  spentPaise: number | null; costPerAdmissionPaise: number | null;
  isReferral: boolean; isPaid: boolean;
}

export interface ChaseView {
  id: string; applicantName: string; why: string; daysOverdue: number; ownerUserId: string | null;
}

export interface FunnelBoardView {
  headline: string;
  openEnquiries: number; admittedThisSeason: number; needChasingToday: number;
  neverContacted: number; stale: number; unsettledEnquiries: number;
  overallConversionPercent: number; medianDaysToAdmit: number | null;
  settlingDays: number;
  sources: SourceLineView[];
  chases: ChaseView[];
  byStage: Record<string, number>;
}

export interface SendCheckView {
  maySend: boolean; refusals: string[]; notBefore: string | null;
}

/** The routes a counsellor screen needs. Every rule behind them lives in the domain. */
export const funnelApi = {
  board: (institutionId?: string) =>
    api.get<FunnelBoardView>(
      `/api/admissions/funnel/board${institutionId ? `?institutionId=${institutionId}` : ''}`),

  get: (id: string) => api.get<EnquiryView>(`/api/admissions/${id}/funnel`),

  capture: (body: Record<string, unknown>) =>
    api.post<{ admission: { id: string }; possibleDuplicates: { id: string; applicantName: string; guardianMobile: string }[] }>(
      '/api/admissions/capture', body),

  logContact: (id: string, body: { channel: string; direction: string; outcome: string; note?: string | null }) =>
    api.post<EnquiryView>(`/api/admissions/${id}/contact`, body),

  followUp: (id: string, on: string | null) =>
    api.post<EnquiryView>(`/api/admissions/${id}/follow-up`, { on }),

  assign: (id: string, ownerUserId: string | null) =>
    api.post<EnquiryView>(`/api/admissions/${id}/assign`, { ownerUserId }),

  attribution: (id: string, body: { channel: string; campaign?: string | null; detail?: string | null }) =>
    api.post<EnquiryView>(`/api/admissions/${id}/attribution`, body),

  /* ⚠ Asked BEFORE anything is typed, so the refusal arrives while there is still time to act on
     it. The gate itself is in the domain — this only reports what it said. */
  mayISend: (id: string, purpose: 'Promotional' | 'Service', channel: string) =>
    api.get<SendCheckView>(`/api/admissions/${id}/may-i-send?purpose=${purpose}&channel=${channel}`),
};

/** The channels, in the order a counsellor would look for them. */
export const ENQUIRY_CHANNELS: { value: string; label: string }[] = [
  { value: 'WalkIn', label: 'Walk-in' },
  { value: 'Phone', label: 'Phone call' },
  { value: 'WhatsApp', label: 'WhatsApp' },
  { value: 'Website', label: 'Website form' },
  { value: 'ParentReferral', label: 'Referred by a parent' },
  { value: 'AlumniReferral', label: 'Referred by an old student' },
  { value: 'StaffReferral', label: 'Referred by staff' },
  { value: 'PartnerReferral', label: 'Referred by a partner school' },
  { value: 'SocialAd', label: 'Social ad' },
  { value: 'SocialOrganic', label: 'Social post' },
  { value: 'SearchAd', label: 'Search ad' },
  { value: 'Print', label: 'Newspaper or pamphlet' },
  { value: 'Outdoor', label: 'Hoarding or banner' },
  { value: 'Outreach', label: 'School visit or fair' },
  { value: 'Other', label: 'Other' },
];

/* ⚠ THE OUTCOMES A COUNSELLOR ACTUALLY PICKS, PER CHANNEL. Offering "Read" beside a phone call, or
   "Busy" beside an email, is how a list gets filled in wrongly and then reported on confidently. */
export const OUTCOMES: Record<string, { value: string; label: string }[]> = {
  Call: [
    { value: 'Spoke', label: 'Spoke to them' },
    { value: 'NoAnswer', label: 'No answer' },
    { value: 'Busy', label: 'Busy' },
    { value: 'Unreachable', label: 'Wrong or dead number' },
    { value: 'OptedOut', label: 'Asked us not to contact' },
  ],
  WhatsApp: [
    { value: 'Spoke', label: 'They replied' },
    { value: 'Read', label: 'Read, no reply' },
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Failed', label: 'Did not go' },
    { value: 'OptedOut', label: 'Asked us to stop' },
  ],
  Sms: [
    { value: 'Spoke', label: 'They replied' },
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Failed', label: 'Did not go' },
    { value: 'OptedOut', label: 'Asked us to stop' },
  ],
  Email: [
    { value: 'Spoke', label: 'They replied' },
    { value: 'Read', label: 'Opened, no reply' },
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Failed', label: 'Bounced' },
    { value: 'OptedOut', label: 'Unsubscribed' },
  ],
  InPerson: [
    { value: 'Spoke', label: 'Met them' },
    { value: 'OptedOut', label: 'Asked us not to contact' },
  ],
};
